import { TreatmentPlan } from '../../../../models/TreatmentPlan';
import { TreatmentPlanDto } from '../../../../types/backendType';
import { ActivityDefinition } from '../../../../types/library';
import { copyToActivitiesInput } from './planCreationUtils';
import { ActivityInput } from './treatmentPlanTypes';

// PAC-127: the patient's "I don't have the equipment right now" moves an exercise to the END of
// the plan on the server, while a clinician may have the same plan open in the editor.
//
// The editor never reloads the plan on its own, and its form cannot tell "the clinician edited
// something" from "the form filled itself in on mount" (FormEquipment writes equipment defaults
// into every row as it renders, which marks the form dirty). So instead of reloading, the patient's
// move is MERGED into whatever is on screen, keeping every edit the clinician has made:
//
//   - an exercise the patient moved goes to the end, unless the clinician has dragged it
//     themselves — then their position wins, because the clinician has the final say;
//   - an exercise the clinician deleted stays deleted;
//   - everything else keeps the clinician's order, additions and settings.
//
// Only a patient-style change is merged: the same exercises in the same slots, with some of them
// moved to the tail. Anything else (another clinician swapped, added or removed an exercise) is
// not ours to fold in silently, so the merge returns null and the save's version check refuses
// the stale save with the existing "This plan changed since you opened it" message.

export type PatientDeferralMerge = {
  // The form's rows, the patient's moves applied. Same row ids as the input.
  activities: ActivityInput[];
  // The new baseline: the loaded rows in the server's new order and positions. Same row ids as
  // before, which is what the save path matches the form's rows to its baseline by.
  baseline: ActivityInput[];
  // The slots the server moved to the end, in the server's order. Empty when the newer version
  // changed no order (e.g. a settings-only save elsewhere).
  movedSlotIds: string[];
};

// The slots `fresh` moved to its tail compared with `baseline`, or null when `fresh` is anything
// other than a patient deferral of `baseline`.
//
// Matched on the deferral's exact signature rather than on "some tail moved", because the server
// changes nothing else: the moved slot gets a position past every other one, and every other slot
// keeps its position. A clinician's save renumbers the whole plan 1..N, so any save of theirs in
// between makes this null — and the save's version check takes over.
export function slotsMovedToEnd(
  baseline: ActivityInput[],
  fresh: ActivityInput[],
): string[] | null {
  if (baseline.length !== fresh.length) {
    return null;
  }
  const baselineBySlot = new Map(baseline.map((row) => [row.slotId, row]));
  const highestLoaded = Math.max(...baseline.map((row) => row.planIndex ?? 0));
  const moved: ActivityInput[] = [];
  for (const row of fresh) {
    const before = row.slotId ? baselineBySlot.get(row.slotId) : undefined;
    if (
      !before ||
      before.planIndex === undefined ||
      row.planIndex === undefined ||
      before.prescribedMovementId !== row.prescribedMovementId
    ) {
      return null;
    }
    if (row.planIndex !== before.planIndex) {
      if (row.planIndex <= highestLoaded) {
        return null;
      }
      moved.push(row);
    }
  }
  return moved
    .sort((a, b) => (a.planIndex as number) - (b.planIndex as number))
    .map((row) => row.slotId as string);
}

// Indices of the longest strictly increasing subsequence of `values`; among equally long ones,
// the one holding the most indices `prefer` accepts. Plans are tens of rows, so quadratic is fine.
function longestIncreasingRun(values: number[], prefer: (i: number) => boolean): Set<number> {
  const length: number[] = values.map(() => 1);
  const preferred: number[] = values.map((_, i) => (prefer(i) ? 1 : 0));
  const previous: number[] = values.map(() => -1);
  for (let i = 0; i < values.length; i++) {
    for (let j = 0; j < i; j++) {
      if (values[j]! >= values[i]!) {
        continue;
      }
      const candidateLength = length[j]! + 1;
      const candidatePreferred = preferred[j]! + (prefer(i) ? 1 : 0);
      if (
        candidateLength > length[i]! ||
        (candidateLength === length[i]! && candidatePreferred > preferred[i]!)
      ) {
        length[i] = candidateLength;
        preferred[i] = candidatePreferred;
        previous[i] = j;
      }
    }
  }
  let end = -1;
  values.forEach((_, i) => {
    if (
      end === -1 ||
      length[i]! > length[end]! ||
      (length[i]! === length[end]! && preferred[i]! > preferred[end]!)
    ) {
      end = i;
    }
  });
  const run = new Set<number>();
  while (end !== -1) {
    run.add(end);
    end = previous[end]!;
  }
  return run;
}

// The loaded slots the clinician has dragged in their edit. A move is read as a change in
// RELATIVE order, and only the smallest set of slots that explains it counts as moved — the slots
// outside the longest run that kept the loaded order — so dragging one exercise past another
// moves the dragged one, not the one it passed. Where both readings are equally small (two
// neighbours trading places), the patient-moved slot counts as the one dragged: the clinician
// wins. Same rule as the patient app's playlist.
function slotsMovedInEdit(
  baseline: ActivityInput[],
  edited: ActivityInput[],
  patientMoved: string[],
): Set<string> {
  const loadedPosition = new Map(baseline.map((row, i) => [row.slotId, i]));
  const kept = edited.filter((row) => row.slotId && loadedPosition.has(row.slotId));
  const positions = kept.map((row) => loadedPosition.get(row.slotId) as number);
  const run = longestIncreasingRun(
    positions,
    (i) => !patientMoved.includes(kept[i]!.slotId as string),
  );
  return new Set(kept.filter((_, i) => !run.has(i)).map((row) => row.slotId as string));
}

export function mergePatientDeferrals(
  baseline: ActivityInput[],
  edited: ActivityInput[],
  fresh: ActivityInput[],
): PatientDeferralMerge | null {
  const movedSlotIds = slotsMovedToEnd(baseline, fresh);
  if (movedSlotIds === null) {
    return null;
  }

  const freshBySlot = new Map(fresh.map((row) => [row.slotId, row]));
  const withFreshPosition = (row: ActivityInput): ActivityInput => {
    const freshRow = row.slotId ? freshBySlot.get(row.slotId) : undefined;
    return freshRow ? { ...row, planIndex: freshRow.planIndex } : row;
  };

  const baselineIdBySlot = new Map(baseline.map((row) => [row.slotId, row.id]));
  const newBaseline = fresh.map((freshRow) => ({
    ...freshRow,
    id: baselineIdBySlot.get(freshRow.slotId) as string,
  }));

  const draggedByClinician = slotsMovedInEdit(baseline, edited, movedSlotIds);
  const toEnd = movedSlotIds.filter(
    (slotId) => !draggedByClinician.has(slotId) && edited.some((row) => row.slotId === slotId),
  );
  const staying = edited.filter((row) => !row.slotId || !toEnd.includes(row.slotId));
  const moving = toEnd.map(
    (slotId) => edited.find((row) => row.slotId === slotId) as ActivityInput,
  );

  return {
    activities: [...staying, ...moving].map(withFreshPosition),
    baseline: newBaseline,
    movedSlotIds,
  };
}

// A plan as the server returns it, as the editor's rows. The same conversion the editor's seeding
// uses, so fresh rows compare like for like with the loaded ones (row ids aside).
export function activitiesOfPlan(
  treatmentPlanDto: TreatmentPlanDto,
  activityDefinitions: Record<string, ActivityDefinition> | undefined,
): ActivityInput[] {
  return copyToActivitiesInput(
    new TreatmentPlan({ treatmentPlanDto, activityDefinitions }).activities,
  );
}

// "Bridges and Planks" — for telling the clinician which exercises the patient moved.
export function movedExerciseNames(
  movedSlotIds: string[],
  rows: ActivityInput[],
  activityDefinitions: Record<string, ActivityDefinition> | undefined,
): string {
  const names = movedSlotIds.map((slotId) => {
    const row = rows.find((r) => r.slotId === slotId);
    return (row && activityDefinitions?.[row.activityDefinitionId]?.name) || 'an exercise';
  });
  return names.length <= 1
    ? names[0] ?? 'an exercise'
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]!}`;
}
